import React, { useState, useEffect, useMemo } from 'react';
import { Select, Spin } from 'antd';
import { useSession } from 'next-auth/react';
import { useFindManyUserUploadedAssetsQuery } from '@src/components/generated/graphql';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';
import styles from './video-selector.module.css';

const { Option } = Select;

const MESSAGES = {
  LOADING: 'Loading videos...',
  NO_VIDEOS: 'No videos uploaded yet. Upload a video first.',
  SELECT_PLACEHOLDER: 'Select video from inventory',
} as const;

interface VideoAsset {
  id: string;
  name: string;
  assetUrl: string;
  assetType: string;
}

interface VideoSelectorProps {
  onVideoSelected: (videoUrl: string) => void;
  currentVideoUrl?: string;
  disabled?: boolean;
}

const isVideoAsset = (asset: any): asset is VideoAsset => {
  return asset?.assetType === ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE;
};

const VideoSelector: React.FC<VideoSelectorProps> = ({
  onVideoSelected,
  currentVideoUrl,
  disabled = false,
}) => {
  const { data: session } = useSession();
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string>('');

  const { data, loading, error } = useFindManyUserUploadedAssetsQuery({
    variables: {
      where: {
        userId: {
          equals: session?.user.id!,
        },
      },
    },
    fetchPolicy: 'no-cache',
    skip: !session?.user?.id,
  });

  const videoAssets = useMemo(() => {
    if (!data?.findManyUserUploadedAssets) return [];
    return data.findManyUserUploadedAssets.filter(isVideoAsset);
  }, [data?.findManyUserUploadedAssets]);

  useEffect(() => {
    if (currentVideoUrl) {
      setSelectedVideoUrl(currentVideoUrl);
    }
  }, [currentVideoUrl]);

  const handleVideoChange = (videoUrl: string): void => {
    setSelectedVideoUrl(videoUrl);
    onVideoSelected(videoUrl);
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size='small' />
        <span>{MESSAGES.LOADING}</span>
      </div>
    );
  }

  if (error) {
    console.error('Error loading videos:', error);
    return (
      <div className={styles.emptyContainer}>
        <span>Error loading videos. Please try again.</span>
      </div>
    );
  }

  if (videoAssets.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <span>{MESSAGES.NO_VIDEOS}</span>
      </div>
    );
  }

  return (
    <div className={styles.videoSelector}>
      <Select
        value={selectedVideoUrl}
        onChange={handleVideoChange}
        placeholder={MESSAGES.SELECT_PLACEHOLDER}
        disabled={disabled}
        style={{ width: '100%' }}
        showSearch
        optionFilterProp='children'
      >
        {videoAssets.map((video) => (
          <Option key={video.id} value={video.assetUrl}>
            {video.name}
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default VideoSelector;
